import { Controller } from 'stimulus'
import Swiper, { EffectFade, Pagination } from 'swiper'
Swiper.use([EffectFade, Pagination])

const IMAGE_SLIDE_DELAY = 3000
const SLIDE_SPEED = 3000
// A swipe hands the rest of its crossfade over on release; at SLIDE_SPEED
// that would drag on long after the finger has let go.
const SWIPE_SPEED = 1000

export default class extends Controller {

  static targets = ['pagination']

  connect() {
    this.swiper = null
    // The slide whose full video is on screen right now.
    this.fullVideoSlide = null
    // The slide whose controls stay hidden until the next transition finishes,
    // so they come back with the slide instead of popping in mid-crossfade.
    this.pendingControlsSlide = null
    this.advanceTimer = null
    // True from the first move of a swipe until the finger or mouse lets go.
    this.dragging = false
    // True while Swiper swaps a cloned slide for its original (see handleLoopFix).
    this.loopFixing = false
    // A preview that finishes mid-swipe waits for the swipe to settle before
    // moving the carousel on, so it never slides out from under the finger.
    this.videoEndedDuringDrag = false
    this.slideCount = this.element.querySelectorAll('.js-heroSlide').length

    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.clearAdvance()
      } else {
        this.scheduleAdvance()
      }
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    this.initHeroSlider()
    this.initVideos()
    this.scheduleAdvance()
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.clearAdvance()
    if (this.swiper) this.swiper.destroy()
  }

  initHeroSlider() {
    // Swiper's own autoplay is deliberately not used here. It would have to be
    // stopped and restarted on nearly every slide, and its start() is a no-op
    // whenever its internal timer handle is still set — which its own pause(),
    // fired before every transition, leaves behind. One timer of ours keeps
    // "images advance on a beat, videos hold until they finish" honest.
    const options = {
      slidesPerView: 1,
      loop: true,
      speed: SLIDE_SPEED,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
    }

    if (this.hasPaginationTarget) {
      options.pagination = { el: this.paginationTarget, clickable: true }
    }

    this.swiper = new Swiper(this.element.querySelector('.js-heroSlider'), options)
    this.swiper.on('slideChange', () => this.handleSlideChange())
    this.swiper.on('slideChangeTransitionEnd', () => this.handleTransitionEnd())
    // A swipe that snaps back to where it started ends here instead.
    this.swiper.on('slideResetTransitionEnd', () => this.resumeAfterDrag())
    this.swiper.on('beforeLoopFix', () => { this.loopFixing = true })
    this.swiper.on('loopFix', () => { this.loopFixing = false })
    this.swiper.on('touchStart', () => this.clearAdvance())
    this.swiper.on('sliderMove', () => { this.dragging = true })
    this.swiper.on('touchEnd', () => this.handleTouchEnd())
  }

  // Swiper clones slides in loop mode, so this has to run after init to reach
  // the clones as well as the originals.
  initVideos() {
    this.element.querySelectorAll('.js-heroPreviewVideo').forEach((video) => {
      // A video slide holds the carousel until its clip finishes, so the
      // preview plays once through rather than looping. With nothing to
      // advance to, a lone slide keeps looping as before.
      video.loop = this.slideCount < 2
      video.addEventListener('ended', () => this.advancePastVideo(video))
      // A clip that never loads would otherwise park the carousel for good.
      video.addEventListener('error', () => this.advancePastVideo(video))
    })

    this.element.querySelectorAll('.js-heroFullVideo').forEach((video) => {
      video.addEventListener('ended', () => {
        const slide = video.closest('.js-heroSlide')
        this.exitFullVideo()
        this.restorePreview(slide)
        this.scheduleAdvance()
      })
    })
  }

  advancePastVideo(video) {
    if (video.loop || this.fullVideoSlide) return
    if (!this.isActiveSlide(video.closest('.js-heroSlide'))) return

    if (this.dragging) {
      this.videoEndedDuringDrag = true
      return
    }

    this.swiper.slideNext()
  }

  handleSlideChange() {
    if (this.loopFixing) {
      this.handleLoopFix()
      return
    }

    this.clearAdvance()
    this.videoEndedDuringDrag = false

    // Drop out of full-video mode as soon as we leave the slide so its audio
    // does not bleed through the crossfade. This is also how a pagination dot
    // clicked mid-video stops it: resetSlides rewinds and hides it.
    if (this.fullVideoSlide) {
      this.pendingControlsSlide = this.fullVideoSlide
      this.exitFullVideo()
    }

    // Coming back to that slide before the crossfade away from it finished
    // leaves it pending, since the interrupted transition never ends. It is
    // now the slide fading in, so its controls should fade in with it.
    if (this.isActiveSlide(this.pendingControlsSlide)) {
      this.pendingControlsSlide = null
    }

    this.resetSlides()
  }

  handleTransitionEnd() {
    this.revealControls()
    this.parkInactivePreviews()
    this.scheduleAdvance()
  }

  // In loop mode Swiper swaps a cloned slide for its original before a swipe
  // (and before slideNext) without animating, which fires slideChange. The
  // same content stays on screen, so rather than resetting everything, the
  // original simply picks up the clone's preview where it was.
  handleLoopFix() {
    const from = this.swiper.slides[this.swiper.previousIndex]
    const to = this.activeSlide()
    if (!from || !to || from === to) return

    const fromPreview = from.querySelector('.js-heroPreviewVideo')
    const toPreview = to.querySelector('.js-heroPreviewVideo')
    if (fromPreview && toPreview) {
      const playing = !fromPreview.paused
      toPreview.currentTime = fromPreview.currentTime
      fromPreview.pause()
      if (playing) {
        toPreview.play().catch(() => {})
      } else {
        toPreview.pause()
      }
    }

    const fromToggle = from.querySelector('.js-heroPlayToggle')
    const toToggle = to.querySelector('.js-heroPlayToggle')
    if (fromToggle && toToggle) {
      toToggle.classList.toggle('is-paused', fromToggle.classList.contains('is-paused'))
      toToggle.setAttribute('aria-label', fromToggle.getAttribute('aria-label'))
    }
  }

  handleTouchEnd() {
    if (this.dragging) {
      this.dragging = false
      this.swiper.params.speed = SWIPE_SPEED
    }

    // Swiper reads the speed for the release synchronously, right after this
    // event. Once it has, the slow crossfade goes back for autoplay and dots.
    // A tap, or a drag that ended where it began, starts no transition at all,
    // so nothing else would bring the carousel back to life.
    queueMicrotask(() => {
      this.swiper.params.speed = SLIDE_SPEED
      if (!this.swiper.animating) this.resumeAfterDrag()
    })
  }

  resumeAfterDrag() {
    if (this.videoEndedDuringDrag) {
      this.videoEndedDuringDrag = false
      this.swiper.slideNext()
      return
    }

    this.scheduleAdvance()
  }

  togglePlay(event) {
    const button = event.currentTarget
    const video = this.previewVideoFor(button)
    if (!video) return

    if (video.paused) {
      video.play()
      button.classList.remove('is-paused')
      button.setAttribute('aria-label', 'Pause video')
    } else {
      video.pause()
      button.classList.add('is-paused')
      button.setAttribute('aria-label', 'Play video')
    }
  }

  watchFull(event) {
    const slide = event.currentTarget.closest('.js-heroSlide')
    if (!slide) return

    const preview = slide.querySelector('.js-heroPreviewVideo')
    const fullVideo = slide.querySelector('.js-heroFullVideo')
    const playToggle = slide.querySelector('.js-heroPlayToggle')
    const watchButton = slide.querySelector('.js-heroWatchFull')
    if (!fullVideo) return

    // Park the carousel for as long as the full video is on screen.
    this.fullVideoSlide = slide
    this.clearAdvance()

    if (preview) {
      preview.pause()
      preview.hidden = true
    }
    if (playToggle) playToggle.hidden = true
    if (watchButton) watchButton.hidden = true

    // Hides the logo/copy overlay so it does not cover the video. The
    // pagination dots stay up so another slide can still be picked.
    this.element.classList.add('is-full-video')

    fullVideo.hidden = false
    fullVideo.currentTime = 0
    fullVideo.play()
  }

  resetSlides() {
    this.element
      .querySelectorAll('.js-heroSlide')
      .forEach((slide) => this.restorePreview(slide))
  }

  restorePreview(slide) {
    if (!slide) return

    const preview = slide.querySelector('.js-heroPreviewVideo')
    const fullVideo = slide.querySelector('.js-heroFullVideo')
    const playToggle = slide.querySelector('.js-heroPlayToggle')
    const watchButton = slide.querySelector('.js-heroWatchFull')

    if (fullVideo) {
      fullVideo.pause()
      fullVideo.currentTime = 0
      fullVideo.hidden = true
    }

    if (preview) {
      preview.hidden = false
      // Only the slide coming on screen restarts its clip. The one fading out
      // is left alone so it does not freeze mid-crossfade; parkInactivePreviews
      // stops it once the transition is done.
      if (this.isActiveSlide(slide)) {
        preview.currentTime = 0
        preview.play().catch(() => {})
      }
    }

    // Only the slide we just left full-video mode on keeps its controls
    // hidden during the transition (revealControls shows them at the end).
    // Every other slide keeps its controls visible so they don't flash in.
    const deferControls = slide === this.pendingControlsSlide
    if (playToggle) {
      playToggle.hidden = deferControls
      playToggle.classList.remove('is-paused')
      playToggle.setAttribute('aria-label', 'Pause video')
    }
    if (watchButton) watchButton.hidden = deferControls
  }

  revealControls() {
    if (!this.pendingControlsSlide) return

    const playToggle = this.pendingControlsSlide.querySelector('.js-heroPlayToggle')
    const watchButton = this.pendingControlsSlide.querySelector('.js-heroWatchFull')
    if (playToggle) playToggle.hidden = false
    if (watchButton) watchButton.hidden = false

    this.pendingControlsSlide = null
  }

  // Once the crossfade is over nothing off screen needs to keep playing.
  parkInactivePreviews() {
    this.element.querySelectorAll('.js-heroPreviewVideo').forEach((video) => {
      if (this.isActiveSlide(video.closest('.js-heroSlide'))) return
      video.pause()
      video.currentTime = 0
    })
  }

  // Image slides move on after a fixed beat. A video slide holds the carousel
  // until its clip finishes, and a full video holds it for as long as it runs.
  scheduleAdvance() {
    this.clearAdvance()

    if (this.slideCount < 2) return
    if (this.fullVideoSlide || this.activePreviewVideo()) return

    this.advanceTimer = setTimeout(() => {
      this.advanceTimer = null
      this.swiper.slideNext()
    }, IMAGE_SLIDE_DELAY)
  }

  clearAdvance() {
    if (!this.advanceTimer) return

    clearTimeout(this.advanceTimer)
    this.advanceTimer = null
  }

  exitFullVideo() {
    this.fullVideoSlide = null
    this.element.classList.remove('is-full-video')
  }

  // Deliberately read activeIndex rather than the swiper-slide-active class:
  // Swiper emits slideChange from updateActiveIndex, which runs *before*
  // updateSlidesClasses, so during that event the class is still sitting on the
  // slide being left. activeIndex is already correct by then.
  activeSlide() {
    if (!this.swiper || !this.swiper.slides) return null

    return this.swiper.slides[this.swiper.activeIndex] || null
  }

  activePreviewVideo() {
    const slide = this.activeSlide()
    return slide ? slide.querySelector('.js-heroPreviewVideo') : null
  }

  isActiveSlide(slide) {
    return !!slide && slide === this.activeSlide()
  }

  previewVideoFor(button) {
    const slide = button.closest('.js-heroSlide')
    return slide ? slide.querySelector('.js-heroPreviewVideo') : null
  }

}
